<template>
  <div class="sci-view-container">
    <!-- Top Glassmorphic Navigation -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button class="sci-btn-back" @click="home">
          <i class="fas fa-arrow-left"></i>
          <span>{{ $t('home') || 'Gestionale' }}</span>
        </button>
        <div class="sci-title-group">
          <h1 class="sci-title">
            <i class="fas fa-chart-pie me-2 text-primary"></i>
            {{ $t('statistiche') || 'Statistiche di Vendita & Analisi Business' }}
          </h1>
          <p class="sci-subtitle">Rendiconto incassi, performance camerieri, piatti più venduti e orari di punta</p>
        </div>
      </div>
      <div class="sci-header-right">
        <button class="sci-btn sci-btn-primary" @click="fetchData" :disabled="loading">
          <i class="fas fa-sync-alt me-1" :class="{ 'fa-spin': loading }"></i>
          <span>{{ loading ? 'Aggiornamento...' : 'Aggiorna Dati' }}</span>
        </button>
      </div>
    </header>

    <!-- Main Spatial Bento Grid -->
    <main class="sci-main-content">
      <!-- Controls & Filter Bento Panel -->
      <section class="sci-card filter-panel mb-4">
        <div class="filter-main-row">
          <!-- Reference Date -->
          <div class="filter-col date-col">
            <label class="sci-label"><i class="fas fa-calendar-day me-1 text-primary"></i> Data di Riferimento:</label>
            <v-date-picker v-model="referenceDate" mode="date" class="sci-date-input" />
          </div>

          <!-- Period Selector -->
          <div class="filter-col period-col">
            <label class="sci-label">Periodo di Analisi:</label>
            <div class="segmented-control">
              <button 
                type="button" 
                class="segment-btn" 
                :class="{ active: period === 'giorno' }" 
                @click="setPeriod('giorno')"
              >
                Giorno
              </button>
              <button 
                type="button" 
                class="segment-btn" 
                :class="{ active: period === 'settimana' }" 
                @click="setPeriod('settimana')"
              >
                Settimana
              </button>
              <button 
                type="button" 
                class="segment-btn" 
                :class="{ active: period === 'mese' }" 
                @click="setPeriod('mese')"
              >
                Mese
              </button>
              <button 
                type="button" 
                class="segment-btn" 
                :class="{ active: period === 'anno' }" 
                @click="setPeriod('anno')"
              >
                Anno
              </button>
            </div>
            <div class="period-caption-pill mt-2">
              <i class="fas fa-info-circle me-1 text-primary"></i>
              <span>{{ currentPeriodLabel }}</span>
            </div>
          </div>
        </div>

        <!-- Secondary Filters: Delivery & Payment -->
        <div class="filter-sub-row mt-3 pt-3 border-top">
          <!-- Delivery Filter -->
          <div class="filter-sub-group">
            <span class="sub-filter-label"><i class="fas fa-motorcycle text-warning me-1"></i> Delivery:</span>
            <div class="pill-group">
              <button 
                type="button" 
                class="filter-pill" 
                :class="{ active: selectedDelivery === 'tutti' }" 
                @click="selectedDelivery = 'tutti'"
              >
                Tutti
              </button>
              <button 
                type="button" 
                class="filter-pill" 
                :class="{ active: selectedDelivery === 'nessuno' }" 
                @click="selectedDelivery = 'nessuno'"
              >
                <i class="fas fa-store me-1"></i> Ristorante
              </button>
              <button 
                v-for="platform in availablePlatforms" 
                :key="platform.nome"
                type="button" 
                class="filter-pill" 
                :class="{ active: selectedDelivery === platform.nome }" 
                @click="selectedDelivery = platform.nome"
              >
                {{ platform.nome }}
              </button>
            </div>
          </div>

          <!-- Payment Filter -->
          <div class="filter-sub-group ms-md-4">
            <span class="sub-filter-label"><i class="fas fa-wallet text-info me-1"></i> Pagamento:</span>
            <div class="pill-group">
              <button 
                type="button" 
                class="filter-pill" 
                :class="{ active: selectedPayment === 'tutti' }" 
                @click="selectedPayment = 'tutti'"
              >
                Tutti
              </button>
              <button 
                type="button" 
                class="filter-pill" 
                :class="{ active: selectedPayment === 'contante' }" 
                @click="selectedPayment = 'contante'"
              >
                <i class="fas fa-money-bill-wave text-success me-1"></i> Contanti
              </button>
              <button 
                type="button" 
                class="filter-pill" 
                :class="{ active: selectedPayment === 'digitale' }" 
                @click="selectedPayment = 'digitale'"
              >
                <i class="fas fa-credit-card text-primary me-1"></i> Digitale
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- 4 Revenue KPI Bento Cards -->
      <section class="kpi-bento-grid mb-4">
        <!-- 1: Incasso Totale -->
        <div class="sci-card kpi-card kpi-total-revenue">
          <div class="kpi-icon-box total-icon">
            <i class="fas fa-coins"></i>
          </div>
          <div class="kpi-info">
            <span class="kpi-label">Incasso Totale</span>
            <span class="kpi-number font-monospace text-success">€ {{ totalRevenue.toFixed(2) }}</span>
            <span class="kpi-subtext text-muted">Fatturato complessivo del periodo</span>
          </div>
        </div>

        <!-- 2: Incasso Tavoli -->
        <div class="sci-card kpi-card">
          <div class="kpi-icon-box table-icon">
            <i class="fas fa-chair"></i>
          </div>
          <div class="kpi-info">
            <span class="kpi-label">Incasso Tavoli</span>
            <span class="kpi-number font-monospace text-primary">€ {{ totalTableRevenue.toFixed(2) }}</span>
            <span class="kpi-subtext text-muted">Consumazioni servite in sala</span>
          </div>
        </div>

        <!-- 3: Incasso Asporto -->
        <div class="sci-card kpi-card">
          <div class="kpi-icon-box takeaway-icon">
            <i class="fas fa-shopping-bag"></i>
          </div>
          <div class="kpi-info">
            <span class="kpi-label">Incasso Asporto</span>
            <span class="kpi-number font-monospace text-info">€ {{ totalAsportoRevenue.toFixed(2) }}</span>
            <span class="kpi-subtext text-muted">Ritiro diretto al banco</span>
          </div>
        </div>

        <!-- 4: Incasso Delivery -->
        <div class="sci-card kpi-card">
          <div class="kpi-icon-box delivery-icon">
            <i class="fas fa-motorcycle"></i>
          </div>
          <div class="kpi-info">
            <span class="kpi-label">Incasso Delivery</span>
            <span class="kpi-number font-monospace text-warning">€ {{ totalDeliveryRevenue.toFixed(2) }}</span>
            <span class="kpi-subtext text-muted">Ordini a domicilio</span>
          </div>
        </div>
      </section>

      <!-- Dashboard Detail Grid -->
      <section class="dashboard-details-grid">
        <!-- 1: Piatti Più Venduti -->
        <div class="sci-card stats-detail-card">
          <h3 class="chart-title">
            <i class="fas fa-trophy text-warning me-2"></i>
            Piatti Più Venduti
          </h3>
          <div class="stats-list mt-3">
            <div 
              v-for="(item, index) in topDishes" 
              :key="item.piatto" 
              class="stats-list-item d-flex align-items-center justify-content-between mb-2"
            >
              <div class="d-flex align-items-center text-truncate me-2">
                <span :class="['rank-badge me-2', 'rank-' + (index + 1)]">#{{ index + 1 }}</span>
                <span class="product-name font-weight-bold text-truncate">{{ item.piatto }}</span>
              </div>
              <span class="badge-qty-sold font-monospace">{{ item.quantita }} venduti</span>
            </div>
            <div v-if="!topDishes.length && !loading" class="no-data-msg text-center py-4 text-muted">
              Nessun piatto venduto nel periodo selezionato
            </div>
          </div>
        </div>

        <!-- 2: Statistiche Camerieri -->
        <div class="sci-card stats-detail-card">
          <h3 class="chart-title">
            <i class="fas fa-users text-primary me-2"></i>
            Statistiche Personale di Sala
          </h3>
          <div class="table-responsive mt-3">
            <table class="sci-table">
              <thead>
                <tr>
                  <th>Cameriere</th>
                  <th style="text-align: center;">Scontrino Medio</th>
                  <th style="text-align: center;">Dolci</th>
                  <th style="text-align: center;">Vini</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="waiter in waiterStats" :key="waiter.cameriere" class="sci-table-row">
                  <td><strong>{{ waiter.cameriere }}</strong></td>
                  <td style="text-align: center;" class="font-monospace">€ {{ waiter.avgReceipt.toFixed(2) }}</td>
                  <td style="text-align: center;"><span class="badge-count">{{ waiter.dolci }}</span></td>
                  <td style="text-align: center;"><span class="badge-count">{{ waiter.vino }}</span></td>
                </tr>
                <tr v-if="!waiterStats.length && !loading">
                  <td colspan="4" class="text-center py-4 text-muted">Nessuna comanda cameriere registrata</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- 3: Giorni con Più Lavoro -->
        <div class="sci-card stats-detail-card">
          <h3 class="chart-title">
            <i class="fas fa-calendar-day text-info me-2"></i>
            Giorni con Maggiore Affluenza
          </h3>
          <div class="stats-list mt-3">
            <div v-for="day in busiestDays" :key="day.name" class="day-stat-row mb-3">
              <div class="d-flex justify-content-between mb-1">
                <span class="day-name fw-bold">{{ day.name }}</span>
                <span class="day-stats text-muted font-monospace small">
                  {{ day.count }} ordini (<strong>€ {{ day.revenue.toFixed(2) }}</strong>)
                </span>
              </div>
              <div class="progress-bar-bg">
                <div 
                  class="progress-bar-fill fill-info" 
                  :style="{ width: day.percentage + '%' }"
                ></div>
              </div>
            </div>
            <div v-if="!busiestDays.some(d => d.count > 0) && !loading" class="no-data-msg text-center py-4 text-muted">
              Nessun dato registrato nei giorni del periodo
            </div>
          </div>
        </div>

        <!-- 4: Orari di Maggior Lavoro -->
        <div class="sci-card stats-detail-card">
          <h3 class="chart-title">
            <i class="fas fa-clock text-success me-2"></i>
            Distribuzione Oraria delle Comande
          </h3>
          <div class="stats-list mt-3">
            <div v-for="hour in busiestHours" :key="hour.name" class="hour-stat-row mb-3">
              <div class="d-flex justify-content-between mb-1">
                <span class="hour-name fw-bold font-monospace">{{ hour.name }}</span>
                <span class="hour-stats text-muted font-monospace small">{{ hour.count }} ordini</span>
              </div>
              <div class="progress-bar-bg">
                <div 
                  class="progress-bar-fill fill-success" 
                  :style="{ width: hour.percentage + '%' }"
                ></div>
              </div>
            </div>
            <div v-if="!busiestHours.some(h => h.count > 0) && !loading" class="no-data-msg text-center py-4 text-muted">
              Nessun dato registrato nelle fasce orarie
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { store } from '../store/store.js'
import moment from 'moment'

export default {
  name: 'Statistiche',
  data() {
    return {
      store,
      loading: false,
      referenceDate: new Date(),
      period: 'mese',
      selectedDelivery: 'tutti',
      selectedPayment: 'tutti',
      availablePlatforms: [],
      topDishes: [],
      waiterStats: [],
      busiestDays: [],
      busiestHours: [],
      totalRevenue: 0,
      totalTableRevenue: 0,
      totalAsportoRevenue: 0,
      totalDeliveryRevenue: 0
    }
  },
  computed: {
    currentPeriodLabel() {
      const mDate = moment(this.referenceDate);
      if (this.period === 'giorno') {
        return mDate.format('DD MMMM YYYY');
      } else if (this.period === 'settimana') {
        const start = mDate.clone().startOf('isoWeek').format('DD/MM/YYYY');
        const end = mDate.clone().endOf('isoWeek').format('DD/MM/YYYY');
        return `Settimana: dal ${start} al ${end}`;
      } else if (this.period === 'mese') {
        return `Mese: ${mDate.format('MMMM YYYY')}`;
      } else if (this.period === 'anno') {
        return `Anno: ${mDate.format('YYYY')}`;
      }
      return '';
    }
  },
  methods: {
    getToken() {
      try {
        if (!localStorage.token) return localStorage.getItem('token') || '';
        const parsed = JSON.parse(localStorage.token);
        return typeof parsed === 'string' ? parsed : (localStorage.getItem('token') || '');
      } catch (e) {
        return localStorage.getItem('token') || '';
      }
    },
    home() {
      this.$router.push({ name: 'Gestionale' });
    },
    setPeriod(p) {
      this.period = p;
      this.fetchData();
    },
    async fetchData() {
      this.loading = true;
      try {
        const mDate = moment(this.referenceDate);
        let start, end;
        
        if (this.period === 'settimana') {
          start = mDate.clone().startOf('isoWeek').unix();
          end = mDate.clone().endOf('isoWeek').unix();
        } else if (this.period === 'mese') {
          start = mDate.clone().startOf('month').unix();
          end = mDate.clone().endOf('month').unix();
        } else if (this.period === 'anno') {
          start = mDate.clone().startOf('year').unix();
          end = mDate.clone().endOf('year').unix();
        } else { // giorno
          start = mDate.clone().startOf('day').unix();
          end = mDate.clone().endOf('day').unix();
        }

        const token = this.getToken();
        const authHeader = `Bearer ${token}`;

        const response = await this.axios.post(this.store.path + 'statistiche/getStatistiche', {
          start,
          end,
          filterDelivery: this.selectedDelivery,
          filterPayment: this.selectedPayment,
          headers: {
            authorization: authHeader
          }
        }, {
          headers: {
            authorization: authHeader
          }
        });
        
        if (response.data) {
          this.topDishes = response.data.topDishes || [];
          this.waiterStats = response.data.waiterStats || [];
          this.busiestDays = response.data.busiestDays || [];
          this.busiestHours = response.data.busiestHours || [];
          this.totalRevenue = response.data.totalRevenue || 0;
          this.totalTableRevenue = response.data.totalTableRevenue || 0;
          this.totalAsportoRevenue = response.data.totalAsportoRevenue || 0;
          this.totalDeliveryRevenue = response.data.totalDeliveryRevenue || 0;
          this.availablePlatforms = response.data.deliveryPlatforms || [];
        }
      } catch (error) {
        console.error('Errore durante il recupero delle statistiche:', error);
      } finally {
        this.loading = false;
      }
    }
  },
  watch: {
    referenceDate() {
      this.fetchData();
    },
    selectedDelivery() {
      this.fetchData();
    },
    selectedPayment() {
      this.fetchData();
    }
  },
  mounted() {
    this.fetchData();
  }
}
</script>

<style scoped>
.sci-view-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--sci-bg, #f8fafc);
  color: var(--sci-text-main, #0f172a);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  padding-bottom: 60px;
}

/* Header */
.sci-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 28px;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--sci-border, #e2e8f0);
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 18px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 12px;
  color: var(--sci-text-muted, #475569);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.22, 1, 0.36, 1);
}

.sci-btn-back:hover {
  background: #f1f5f9;
  color: var(--sci-text-main, #0f172a);
  border-color: #cbd5e1;
}

.sci-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
}

.sci-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.82rem;
  color: var(--sci-text-muted, #475569);
}

.sci-btn-primary {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 20px;
  background: var(--sci-primary, #4f46e5);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.sci-btn-primary:hover:not(:disabled) {
  background: #4338ca;
}

.sci-main-content {
  max-width: 1360px;
  margin: 0 auto;
  width: 100%;
  padding: 24px 20px;
}

/* Card */
.sci-card {
  background: #ffffff;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 18px;
  box-shadow: 0 4px 6px -1px rgba(15, 23, 42, 0.05);
  padding: 22px;
}

/* Filter Panel */
.filter-main-row {
  display: flex;
  gap: 32px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.sci-label {
  display: block;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--sci-text-muted, #475569);
  margin-bottom: 8px;
}

.segmented-control {
  display: inline-flex;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 12px;
  gap: 4px;
}

.segment-btn {
  border: none;
  background: transparent;
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--sci-text-muted, #475569);
  cursor: pointer;
  transition: all 0.2s;
}

.segment-btn:hover {
  color: var(--sci-text-main, #0f172a);
}

.segment-btn.active {
  background: #ffffff;
  color: var(--sci-primary, #4f46e5);
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}

.period-caption-pill {
  display: inline-flex;
  align-items: center;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--sci-text-muted, #475569);
  background: #f8fafc;
  padding: 4px 10px;
  border-radius: 8px;
}

.filter-sub-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.filter-sub-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.sub-filter-label {
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--sci-text-muted, #475569);
}

.pill-group {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.filter-pill {
  border: 1px solid var(--sci-border, #e2e8f0);
  background: #f8fafc;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--sci-text-muted, #475569);
  cursor: pointer;
  transition: all 0.2s;
}

.filter-pill:hover {
  background: #eef2ff;
  color: var(--sci-primary, #4f46e5);
  border-color: #c7d2fe;
}

.filter-pill.active {
  background: var(--sci-primary, #4f46e5);
  color: #ffffff;
  border-color: var(--sci-primary, #4f46e5);
}

/* KPI Grid */
.kpi-bento-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
}

.kpi-card {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 20px;
}

.kpi-icon-box {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  flex-shrink: 0;
}

.total-icon { background: #ecfdf5; color: #059669; }
.table-icon { background: #eef2ff; color: #4f46e5; }
.takeaway-icon { background: #f0f9ff; color: #0284c7; }
.delivery-icon { background: #fffbeb; color: #d97706; }

.kpi-info {
  display: flex;
  flex-direction: column;
}

.kpi-label {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--sci-text-muted, #475569);
  letter-spacing: 0.4px;
}

.kpi-number {
  font-size: 1.45rem;
  font-weight: 800;
  margin: 3px 0 2px 0;
}

.kpi-subtext {
  font-size: 0.78rem;
}

/* Details Grid */
.dashboard-details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

@media (max-width: 992px) {
  .dashboard-details-grid {
    grid-template-columns: 1fr;
  }
}

.chart-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
}

.stats-list {
  max-height: 420px;
  overflow-y: auto;
  padding-right: 4px;
}

.stats-list-item {
  background: #f8fafc;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid var(--sci-border, #e2e8f0);
}

.product-name {
  color: var(--sci-text-main, #0f172a);
}

.badge-qty-sold {
  color: #059669;
  font-weight: 700;
  font-size: 0.88rem;
}

.rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  font-weight: 800;
  font-size: 0.78rem;
  color: white;
  background: #94a3b8;
  flex-shrink: 0;
}

.rank-1 { background: #f59e0b; }
.rank-2 { background: #64748b; }
.rank-3 { background: #b45309; }

.badge-count {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  background: #f1f5f9;
  font-weight: 700;
  font-size: 0.82rem;
}

.progress-bar-bg {
  width: 100%;
  height: 8px;
  background: #f1f5f9;
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.4s ease;
}

.fill-info { background: #0284c7; }
.fill-success { background: #10b981; }

.sci-table {
  width: 100%;
  border-collapse: collapse;
}

.sci-table th {
  padding: 10px 12px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--sci-text-muted, #475569);
  border-bottom: 2px solid var(--sci-border, #e2e8f0);
}

.sci-table-row td {
  padding: 10px 12px;
  border-bottom: 1px solid #f1f5f9;
}
</style>
